import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';

export type GenericColumn = {
  key: string;
  label: string;
  type: 'number' | 'string';
};

export type GenericRow = Record<string, string | number>;

export type GenericStatsTableProps = {
  title: string;
  subtitle?: string;
  columns: GenericColumn[];
  rows: GenericRow[];
  linkKey?: string;
  onLinkClick?: (value: string) => void;
  defaultSort?: { key: string; direction: 'asc' | 'desc' };
  efficiencyKey?: string;
  rankKey?: string;
  infoContent?: Record<string, { title: string; description: ReactNode }>;
};

export function GenericStatsTable({
  title,
  subtitle,
  columns,
  rows,
  linkKey,
  onLinkClick,
  defaultSort,
  efficiencyKey = 'efficiency',
  rankKey = 'rank',
  infoContent: infoOverrides,
}: GenericStatsTableProps): JSX.Element {
  const infoContent: Record<string, { title: string; description: ReactNode }> = {
    medic: {
      title: 'Медицина',
      description: 'Показатель помощи союзникам за игру: лечение, эвакуация и стабилизация.',
    },
    ego: {
      title: 'ЧСВ',
      description: 'Индекс личной результативности с учётом фрагов, смертей и участия в бою.',
    },
    efficiency: {
      title: 'КПД',
      description: 'Общий коэффициент полезности: вклад, выживаемость и командная дисциплина.',
    },
  };
  const mergedInfoContent = infoOverrides ? { ...infoContent, ...infoOverrides } : infoContent;

  const initialSort = defaultSort ?? { key: efficiencyKey, direction: 'desc' as const };
  const [sortKey, setSortKey] = useState<string>(initialSort.key);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(initialSort.direction);
  const [activeInfoKey, setActiveInfoKey] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    columns.forEach((col) => {
      initial[col.key] = '';
    });
    return initial;
  });

  const efficiencyRange = useMemo(() => {
    if (!rows.length || !columns.some((c) => c.key === efficiencyKey)) {
      return { min: 0, max: 1 };
    }
    const numericValues = rows
      .map((row) => Number(row[efficiencyKey]))
      .filter((val) => !Number.isNaN(val));
    return {
      min: Math.min(...numericValues),
      max: Math.max(...numericValues),
    };
  }, [rows, columns, efficiencyKey]);

  const handleSort = (key: string) => {
    setSortKey(key);
    setSortDirection((prev) => (key === sortKey ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const filteredAndSorted = useMemo(() => {
    const filtered = rows.filter((row) =>
      columns.every((col) => {
        const query = filters[col.key]?.trim();
        if (!query) return true;
        const value = row[col.key];
        return value
          .toString()
          .toLowerCase()
          .includes(query.toLowerCase());
      })
    );

    const sorted = [...filtered].sort((a, b) => {
      const colMeta = columns.find((col) => col.key === sortKey);
      if (!colMeta) return 0;
      const aVal = a[sortKey];
      const bVal = b[sortKey];

      if (colMeta.type === 'number') {
        const diff = Number(aVal) - Number(bVal);
        return sortDirection === 'asc' ? diff : -diff;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr === bStr) return 0;
      const result = aStr > bStr ? 1 : -1;
      return sortDirection === 'asc' ? result : -result;
    });

    return sorted;
  }, [columns, filters, rows, sortDirection, sortKey]);

  const renderSortIndicator = (key: string) => {
    if (sortKey !== key) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const kpiStyle = (value: number) => {
    const spread = Math.max(efficiencyRange.max - efficiencyRange.min, 0.001);
    const norm = Math.max(0, Math.min(1, (value - efficiencyRange.min) / spread));
    const startAlpha = 0.18 + norm * 0.25;
    const endAlpha = 0.32 + norm * 0.45;

    return {
      background: `linear-gradient(90deg, rgba(255, 222, 0, ${startAlpha.toFixed(3)}) 0%, rgba(255, 222, 0, ${endAlpha.toFixed(
3)}) 100%)`,
      color: '#0f1115',
    } as const;
  };

  const renderCell = (row: GenericRow, colKey: string) => {
    const value = row[colKey];
    const isRank = colKey === rankKey;

    if (linkKey && colKey === linkKey) {
      const text = String(value);
      return (
        <a
          href={`/player/${encodeURIComponent(text)}`}
          className="inline-flex items-center justify-center gap-2 rounded px-2 py-1 font-semibold hover:underline"
          onClick={(event) => {
            event.preventDefault();
            onLinkClick?.(text);
          }}
          aria-label={`Открыть статистику игрока ${text}`}
        >
          {text}
        </a>
      );
    }

    return (
      <span
        className={`inline-flex w-full items-center justify-center ${isRank ? 'font-semibold text-foreground dark:text-primary' : ''}`}
      >
        {value}
      </span>
    );
  };

  useEffect(() => {
    if (!activeInfoKey) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-info-key]')) return;
      setActiveInfoKey(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [activeInfoKey]);

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        <h2 className="text-3xl font-bold">{title}</h2>
      </div>
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                {columns.map((col) => (
                  <th key={col.key} className="px-3 py-3 text-center align-middle font-semibold">
                    <div className="mx-auto flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleSort(col.key)}
                        className="flex items-center justify-center gap-2 rounded-md px-2 py-1 text-foreground transition hover:bg-muted/70"
                      >
                        <span>{col.label}</span>
                        <span className="text-muted-foreground">{renderSortIndicator(col.key)}</span>
                      </button>
                      {mergedInfoContent[col.key] ? (
                        <div className="relative" data-info-key={col.key}>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              setActiveInfoKey((prev) => (prev === col.key ? null : col.key));
                            }}
                            className="flex h-5 w-5 items-center justify-center rounded-full border border-border text-[10px] font-semibold text-muted-foreground transition hover:text-foreground"
                            aria-label={`Справка: ${col.label}`}
                            aria-expanded={activeInfoKey === col.key}
                          >
                            ?
                          </button>
                          {activeInfoKey === col.key ? (
                            <div className="absolute right-0 top-7 z-20 w-64 rounded-md border border-border bg-card p-3 text-left text-xs text-foreground shadow-lg">
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-sm font-semibold text-foreground">
                                  {mergedInfoContent[col.key].title}
                                </p>
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setActiveInfoKey(null);
                                  }}
                                  className="flex h-6 w-6 items-center justify-center rounded-full text-sm text-muted-foreground transition hover:bg-muted/60 hover:text-foreground"
                                  aria-label="Закрыть подсказку"
                                >
                                  ✕
                                </button>
                              </div>
                              <div className="mt-1 text-muted-foreground">{mergedInfoContent[col.key].description}</div>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </th>
                ))}
              </tr>
              <tr>
                {columns.map((col) => (
                  <th key={`${col.key}-filter`} className="px-3 pb-3 text-center align-middle">
                    <input
                      type="text"
                      value={filters[col.key]}
                      onChange={(e) => handleFilterChange(col.key, e.target.value)}
                      placeholder="Поиск"
                      className="w-full rounded-md border border-border bg-background px-2 py-1 text-center text-xs text-foreground focus:border-primary focus:outline-none"
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredAndSorted.map((row, idx) => (
                <motion.tr
                  key={`${row[linkKey ?? rankKey] ?? idx}-${idx}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18, delay: idx * 0.02 }}
                  className={`${idx % 2 === 0 ? 'bg-card' : 'bg-card/60'} transition-colors hover:bg-muted/40`}
                >
                  {columns.map((col) => {
                    const isEfficiency = col.key === efficiencyKey && typeof row[col.key] === 'number';
                    return (
                      <td
                        key={col.key}
                        className={`px-3 py-3 text-center align-middle${isEfficiency ? ' kpi-highlight font-semibold rounded' : ''}`}
                        style={isEfficiency ? kpiStyle(Number(row[col.key])) : undefined}
                      >
                        {renderCell(row, col.key)}
                      </td>
                    );
                  })}
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
